import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import type { RootState } from '../store';
import axiosInstance from '../../_helpers/axios_instance';
import { isAdmin, isOrgLeader } from './authentication_slice';

export interface Contact {
  role: string;
  [key: string]: unknown;
}

export interface Group {
  id: number;
  name: string;
  enabled: boolean;
  pwi_enabled?: boolean;
  // Per-group control over the insights Specialty filter: whether to offer it at
  // all, and what to label it. A blank label falls back to "Specialty".
  specialty_filter_enabled?: boolean;
  specialty_filter_label?: string | null;
  logo_url?: string;
  contacts?: Contact[];
  [key: string]: unknown;
}

type AsyncStatus = 'idle' | 'loading' | 'succeeded' | 'failed';

interface GroupsState {
  groups: Group[];
  fetchingGroups: boolean;
  groupsFetchError: string | string[] | null;
  currentGroup: Group | null;
  // Demographic values actually held by users in the group being viewed, used to
  // narrow the insights filter dropdowns. Departments are raw burnout_location
  // strings — free text — so consumers intersect them with the canonical list.
  rolesInUse: string[];
  departmentsInUse: string[];
  // Raw burnout_specialty strings in use by the group. Unlike departments these
  // are NOT intersected with a canonical list: the vocabulary is group-specific
  // (that's why the label is configurable per group, and why anaUserOptions
  // repurposes burnout_specialty entirely), so there's no shared list to filter against.
  specialtiesInUse: string[];
  demographicsInUseGroupId: number | null;
  demographicsInUseStatus: AsyncStatus;
}

let currentGroupFromStorage: Group | null = null;
try {
  const item = localStorage.getItem('currentGroup');
  currentGroupFromStorage = item ? JSON.parse(item) : null;
} catch {
  localStorage.removeItem('currentGroup');
}

const initialState: GroupsState = {
  groups: [],
  fetchingGroups: false,
  groupsFetchError: null,
  currentGroup: currentGroupFromStorage,
  rolesInUse: [],
  departmentsInUse: [],
  specialtiesInUse: [],
  demographicsInUseGroupId: null,
  demographicsInUseStatus: 'idle',
};

export const getGroups = createAsyncThunk<Group[], void, { state: RootState }>(
  'groups/getGroups',
  async (_, { dispatch, rejectWithValue }) => {
    try {
      const response = await axiosInstance.get('/api/practice_groups');
      const fetchedGroups: Group[] = response.data.data
        ? response.data.data.filter((g: Group) => g.enabled)
        : response.data.filter((g: Group) => g.enabled);

      if (fetchedGroups.length === 1 && localStorage.getItem('currentGroup') === null) {
        dispatch(selectGroup(fetchedGroups[0]));
      }
      return fetchedGroups;
    } catch (error: any) {
      if (error.response) return rejectWithValue(error.response.data.errors || 'Failed to fetch groups');
      if (error.request) return rejectWithValue(error.message || 'Network error, no response received');
      return rejectWithValue(error.message || 'Error in request setup');
    }
  },
  {
    condition: (_, { getState }) => !getState().groups.fetchingGroups,
  }
);

// Which demographic values the group's users actually have, for narrowing the
// insights filter dropdowns to options that can match something.
export const fetchDemographicsInUse = createAsyncThunk<
  { roles: string[]; departments: string[]; specialties: string[]; groupId: number },
  number,
  { state: RootState }
>(
  'groups/fetchDemographicsInUse',
  async (groupId, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.get('/api/insights/filter_options', {
        params: { practice_group_id: groupId },
      });
      return {
        roles: response.data?.data?.roles ?? [],
        departments: response.data?.data?.departments ?? [],
        specialties: response.data?.data?.specialties ?? [],
        groupId,
      };
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.errors || error.message);
    }
  },
  {
    condition: (groupId, { getState }) => {
      const s = getState().groups;
      if (s.demographicsInUseStatus === 'loading') return false;
      // Already have this group's values — don't refetch on every filter render.
      return !(s.demographicsInUseGroupId === groupId && s.demographicsInUseStatus === 'succeeded');
    },
  }
);

// A user reaches at most one group — practice_providers -> practices ->
// practice_groups — so `practice_groups` on their record holds 0 or 1 entries and
// there is nothing to pick between. Adopt it as the current group.
//
// getGroups already auto-selects a sole group, but only ever runs for admins and
// org leaders: its one caller is LoadingModal, which MainPage mounts behind an
// is_admin || is_org_leader check. A provider therefore reached no fetch, no
// picker, and no currentGroup at all. This needs no request — the group is
// already on the user payload.
//
// An existing selection always wins, so an admin viewing as another group is
// never pulled back to their own.
export const autoSelectOwnGroup = createAsyncThunk<Group | null, void, { state: RootState }>(
  'groups/autoSelectOwnGroup',
  async (_, { getState, dispatch }) => {
    const { groups, authentication } = getState();
    if (groups.currentGroup) return null;

    const ownGroup = authentication.user?.practice_groups?.[0];
    if (!ownGroup) return null;

    await dispatch(selectGroup(ownGroup));
    return ownGroup;
  }
);

export const selectGroup = createAsyncThunk<Group, Group>(
  'groups/selectGroup',
  async (group) => {
    if (group && typeof group === 'object') {
      localStorage.setItem('currentGroup', JSON.stringify(group));
      return group;
    }
    return group;
  }
);

export const resetGroup = createAsyncThunk<void, void, { state: RootState }>(
  'groups/resetGroup',
  async (_, { dispatch }) => {
    localStorage.removeItem('currentGroup');
    localStorage.removeItem('currentBlock');
    dispatch(getGroups());
  }
);

const groupsSlice = createSlice({
  name: 'groups',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(getGroups.pending, (state) => {
        state.fetchingGroups = true;
        state.groupsFetchError = null;
      })
      .addCase(getGroups.fulfilled, (state, action) => {
        state.fetchingGroups = false;
        state.groups = action.payload;
        state.groupsFetchError = null;
      })
      .addCase(getGroups.rejected, (state, action) => {
        state.fetchingGroups = false;
        state.groupsFetchError = (action.payload as string | string[]) ?? action.error.message ?? null;
      })
      .addCase(selectGroup.fulfilled, (state, action) => {
        state.currentGroup = action.payload;
      })
      .addCase(resetGroup.fulfilled, (state) => {
        state.currentGroup = null;
      })
      .addCase(fetchDemographicsInUse.pending, (state) => {
        state.demographicsInUseStatus = 'loading';
      })
      .addCase(fetchDemographicsInUse.fulfilled, (state, action) => {
        state.demographicsInUseStatus = 'succeeded';
        state.rolesInUse = action.payload.roles;
        state.departmentsInUse = action.payload.departments;
        state.specialtiesInUse = action.payload.specialties;
        state.demographicsInUseGroupId = action.payload.groupId;
      })
      .addCase(fetchDemographicsInUse.rejected, (state) => {
        // Leave both lists empty so consumers fall back to their full lists.
        state.demographicsInUseStatus = 'failed';
        state.rolesInUse = [];
        state.departmentsInUse = [];
        state.specialtiesInUse = [];
      });
  },
});

export default groupsSlice.reducer;

const selectGroupsState = (state: { groups: GroupsState }) => state.groups;
export const selectAllGroups = (state: { groups: GroupsState }) => selectGroupsState(state).groups;
export const selectFetchingGroups = (state: { groups: GroupsState }) => selectGroupsState(state).fetchingGroups;
export const selectGroupsFetchError = (state: { groups: GroupsState }) => selectGroupsState(state).groupsFetchError;
export const selectCurrentGroup = (state: { groups: GroupsState }) => selectGroupsState(state).currentGroup;

export const selectCurrentGroupName = (state: { groups: GroupsState }): string => {
  const currentGroup = selectCurrentGroup(state);
  return currentGroup?.name ?? '';
};

export const selectCurrentGroupId = (state: { groups: GroupsState }): number => {
  const currentGroup = selectCurrentGroup(state);
  return currentGroup?.id ?? 0;
};

/**
 * The group lens: the group whose My Metrics story a group picker (an admin or
 * org leader, who choose the group they view) is looking at, or null. The API
 * reads it as viewing_group_id on the account-wide GETs My Metrics shows
 * (the my_metrics figures, time_watched, user_points, user_achievements and
 * cme_credits), only in the demo environment, to answer a viewer of Demo Group
 * with the demo story. The CE claim POST carries it too, so the API saves no
 * claim for a story finish. It never widens whose data is read, and providers
 * never send it: the API classifies them by their own groups.
 *
 * A demo build only. The flag is spelled out inline, like the one in
 * profile.tsx, so that without it Rollup folds this to null and no request
 * anywhere carries the lens. Keep it the one place REACT_APP_DEMO_LENS is read.
 *
 * Anything loaded for another lens is dropped (index.ts keepToLens), so a
 * request that sends this must record the lens its answer belongs to.
 */
export const selectLensGroupId = (state: Pick<RootState, 'authentication' | 'groups'>): number | null =>
  process.env.REACT_APP_DEMO_LENS === 'true' && (isAdmin(state.authentication) || isOrgLeader(state.authentication))
    ? state.groups.currentGroup?.id ?? null
    : null;

/** The query params that carry a lens: none without one. */
export const lensParams = (lensGroupId: number | null): { viewing_group_id?: number } =>
  lensGroupId === null ? {} : { viewing_group_id: lensGroupId };

/** Where a lens-dependent response records the lens it was requested for. */
export interface LensMeta {
  lensGroupId: number | null;
}

/** Whether a response was requested for the lens an entry holds (a hand-built
 *  action with no meta, and an entry never tagged, both mean no lens). */
export const isForLens = (action: { meta?: Partial<LensMeta> }, entryLensGroupId: number | null | undefined): boolean =>
  (action.meta?.lensGroupId ?? null) === (entryLensGroupId ?? null);

export const selectCurrentGroupLogoUrl = (state: { groups: GroupsState }): string => {
  const currentGroup = selectCurrentGroup(state);
  return currentGroup?.logo_url ?? '';
};

// PWI is a per-customer add-on; entitlement follows the currently selected practice
// group (admins pick any group; single-group users are auto-selected).
export const selectCurrentGroupPwiEnabled = (state: { groups: GroupsState }): boolean => {
  const currentGroup = selectCurrentGroup(state);
  return currentGroup?.pwi_enabled ?? false;
};

export const selectInHouseResource = (state: { groups: GroupsState }): Contact | undefined => {
  const currentGroup = selectCurrentGroup(state);
  return currentGroup?.contacts?.find((contact) => contact.role === 'primary');
};

export const selectRolesInUse = (state: { groups: GroupsState }): string[] =>
  selectGroupsState(state).rolesInUse;

export const selectDepartmentsInUse = (state: { groups: GroupsState }): string[] =>
  selectGroupsState(state).departmentsInUse;

export const selectSpecialtiesInUse = (state: { groups: GroupsState }): string[] =>
  selectGroupsState(state).specialtiesInUse;

// Whether an EC record's practice-group ties include one of the viewer's groups.
// Records tied to other groups never reach the client, but match explicitly so
// an admin viewing as another group sees that group's content, not their own.
export const isTiedToGroup = (
  practiceGroupIds: Array<string | number> | undefined,
  relevantGroupIds: Array<string | number>
): boolean =>
  Array.isArray(practiceGroupIds) &&
  practiceGroupIds.some((id) => relevantGroupIds.some((groupId) => String(groupId) === String(id)));

// Narrowing rules for the insights filters, kept as plain functions so they can
// be composed into cross-slice selectors and tested without a store.

// Roles: the group's in-use titles when we have them, otherwise every enabled
// title. The fallback covers a group whose users have no titles set, a failed
// request, and the moment before the fetch resolves — in each case a full list
// beats an empty dropdown.
export const insightRoleOptions = (rolesInUse: string[], allEnabled: string[]): string[] =>
  rolesInUse.length > 0 ? rolesInUse : allEnabled;

// Departments: canonical entries that at least one user in the group actually
// has. Driving off the canonical list keeps free-text values (cost-centre codes
// and the like) out and preserves its curated order.
export const insightDepartmentOptions = (
  departmentsInUse: string[],
  canonicalDepartments: string[]
): string[] => {
  const inUse = new Set(departmentsInUse);
  const matching = canonicalDepartments.filter((d) => inUse.has(d));
  return matching.length > 0 ? matching : canonicalDepartments;
};

// Specialties: whatever the group's users actually have, as-is. There is no
// canonical list to fall back to the way Role and Department do — each group
// uses its own specialty vocabulary, which is the reason the filter's label is
// configurable per group. An empty list leaves the dropdown at just "All".
export const insightSpecialtyOptions = (specialtiesInUse: string[]): string[] => specialtiesInUse;

export const DEFAULT_SPECIALTY_FILTER_LABEL = 'Specialty';

// Whether to offer the Specialty filter for the group being viewed, and what to
// call it. Prefers the group as most recently fetched over `currentGroup`, which
// is restored from localStorage and only replaced when an admin re-picks a group
// — without this, a cached selection would hide the filter until they did so.
export interface SpecialtyFilterSource {
  specialty_filter_enabled?: boolean;
  specialty_filter_label?: string | null;
}

export const specialtyFilterSettingsForGroup = (
  group: SpecialtyFilterSource | null | undefined
): { enabled: boolean; label: string } => ({
  enabled: group?.specialty_filter_enabled ?? false,
  label: group?.specialty_filter_label?.trim() || DEFAULT_SPECIALTY_FILTER_LABEL,
});

export const specialtyFilterSettings = (
  groups: Group[],
  currentGroup: Group | null
): { enabled: boolean; label: string } => {
  const fresh = currentGroup ? groups.find((g) => g.id === currentGroup.id) : undefined;
  return specialtyFilterSettingsForGroup(fresh ?? currentGroup);
};
